import { closeTopmostModal } from '../android/androidBackStack';

/**
 * P-081 · el Atrás del NAVEGADOR en el punto de venta regresa un nivel, como el del teléfono.
 *
 * En la app (APK) el Atrás físico lo atiende Capacitor (`android/phoneBack.ts`): primero cierra lo que esté más
 * arriba en la pila (`androidBackStack.ts`) —un diálogo, la hoja del pedido, o el nivel del cobro que registra el
 * punto de venta— y solo después va al Inicio. En la web abierta en Chrome el Atrás es el historial, y del cobro o
 * de la hoja del pedido sacaba del punto de venta (FI-102, FI-106).
 *
 * Igual que «Nuevo producto» (FI-93, `newProductRoute.ts`): mientras haya un nivel al que regresar, hay UNA entrada
 * más en el historial encima de la del punto de venta, con la misma dirección (sin `hashchange`): la guarda. El
 * Atrás del navegador la saca y aquí se hace lo mismo que el Atrás del teléfono: cerrar lo de más arriba de la
 * misma pila. Si sigue habiendo un nivel, la guarda vuelve a ponerse. Cuando ya no hay nivel (se cobró, se volvió al
 * catálogo), la guarda que sobra se quita con un Atrás silencioso, para que el siguiente Atrás sea el de siempre.
 *
 *   · Nunca hay más de una guarda; una que ya está (recarga) se adopta.
 *   · Si el Atrás lleva a otra pantalla (otra dirección), no se hace nada: el router ya se va.
 *   · Solo en la web: en la app no se arma (el Atrás físico no usa el historial).
 *
 * P-081-C1 · VOLVER A PONER LA GUARDA ES UN ADELANTE, NO UNA ENTRADA NUEVA. Chrome (la «intervención de manipulación
 * del historial») marca para saltar TODAS las entradas de una página que crea una entrada nueva sin que la persona
 * haya tocado la pantalla desde el último Atrás; su botón Atrás se las salta y sale de la app. Volver a poner la
 * guarda con `pushState` justo después de un Atrás era eso: medido en el Chrome del emulador, el segundo Atrás del
 * cobro salía de Negocio360. La guarda que el Atrás sacó sigue DELANTE en el historial: se vuelve a ella con
 * `history.forward()`, que no crea nada. `pushState` queda solo para la primera guarda, que siempre nace de un toque
 * (abrir el cobro, la hoja o un diálogo). `history.back()` y el harness no ven esta regla; el botón del sistema sí.
 */
const GUARD_KEY = 'n360PosGuard';
/** Si el Adelante propio no llega (no había guarda delante), se deja de esperarlo. */
const RESTORE_TIMEOUT_MS = 1000;

/** ¿La entrada actual del historial es la guarda del punto de venta? */
export function onPosBrowserGuard(): boolean {
  const state: unknown = window.history.state;
  return Boolean(state && typeof state === 'object' && (state as Record<string, unknown>)[GUARD_KEY] === true);
}

function stateWithout(): Record<string, unknown> {
  const state: unknown = window.history.state;
  return state && typeof state === 'object' ? { ...(state as Record<string, unknown>) } : {};
}

/**
 * Mantiene la guarda mientras `hasLevel()` diga que hay un nivel al que regresar. Devuelve con qué dejar de escuchar.
 * `sameScreen(hash)` dice si una dirección es la del punto de venta (si el Atrás se fue a otra, no se toca nada).
 */
export function watchPosBrowserBack(hasLevel: () => boolean, sameScreen: (hash: string) => boolean): { sync: () => void; stop: () => void } {
  // Un Atrás silencioso propio (quitar una guarda que sobra) está en camino: su `popstate` no es de la persona, y
  // mientras llega no se pide otro (Codex: dos sincronizaciones seguidas pedían dos y descuadraban la cuenta).
  let removing = false;
  // P-081-C1: un Adelante propio (volver a la guarda que quedó delante) está en camino; igual que el anterior.
  let restoring = false;
  // P-081-C1: la guarda quedó DELANTE de la entrada actual (la sacó un Atrás, de la persona o el silencioso).
  let guardAhead = false;
  let stopped = false;
  let pending: number | undefined;
  let restoreTimer: number | undefined;
  const here = () => sameScreen(window.location.hash);
  const arm = () => {
    if (stopped || removing || restoring || !here() || onPosBrowserGuard() || !hasLevel()) return;
    if (guardAhead) {
      guardAhead = false;
      restoring = true;
      // Si no había nada delante, no llega ningún `popstate`: se deja de esperar y NO se crea una entrada (sin un toque
      // reciente, Chrome marcaría la app para saltarla). Sin guarda, el Atrás solo sale del punto de venta, como antes.
      restoreTimer = window.setTimeout(() => { restoreTimer = undefined; restoring = false; }, RESTORE_TIMEOUT_MS);
      window.history.forward();
      return;
    }
    window.history.pushState({ ...stateWithout(), [GUARD_KEY]: true }, '');
  };
  const sync = () => {
    if (stopped || removing || restoring || !here()) return;
    if (hasLevel()) { arm(); return; }
    if (onPosBrowserGuard()) { removing = true; window.history.back(); }
  };
  const onPop = () => {
    if (stopped) return;
    if (removing) { removing = false; guardAhead = true; return; }
    if (restoring) {
      restoring = false;
      if (restoreTimer !== undefined) { window.clearTimeout(restoreTimer); restoreTimer = undefined; }
      // De vuelta sobre la guarda: si mientras tanto el nivel se cerró, se quita como siempre.
      if (onPosBrowserGuard()) pending = window.setTimeout(() => { pending = undefined; sync(); }, 0);
      return;
    }
    if (!here()) { guardAhead = false; return; }
    // Adelante, de vuelta sobre una guarda: no es un Atrás.
    if (onPosBrowserGuard()) { guardAhead = false; return; }
    guardAhead = true;
    closeTopmostModal();
    // El nivel se cierra con un cambio de estado; la guarda se decide cuando ya se dibujó.
    pending = window.setTimeout(() => { pending = undefined; sync(); }, 0);
  };
  window.addEventListener('popstate', onPop);
  return {
    sync,
    stop: () => {
      stopped = true;
      if (pending !== undefined) window.clearTimeout(pending);
      if (restoreTimer !== undefined) window.clearTimeout(restoreTimer);
      window.removeEventListener('popstate', onPop);
    },
  };
}
